import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, RotateCcw, Clock, X } from 'lucide-react';
import { CATEGORY_OPTIONS_MAP, ALL_CATEGORIES, ALL_ORDERERS, ALL_CONTRACT_TYPES } from '@shared/constants/categoryOptions';

const STAFF_OPTIONS = ['이현진', '류지민', '장지안', '김호정', '박유찬', '확인불가'];

const HISTORY_KEY = 'town-search-history';
const MAX_HISTORY = 10;

function getHistory(): string[] {
    try {
        const raw = localStorage.getItem(HISTORY_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch {
        return [];
    }
}

function saveHistory(history: string[]) {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

function addToHistory(term: string) {
    if (!term.trim()) return;
    const history = getHistory().filter((h) => h !== term);
    history.unshift(term);
    saveHistory(history);
}

function removeFromHistory(term: string) {
    const history = getHistory().filter((h) => h !== term);
    saveHistory(history);
}

interface SearchFilterBarProps {
    search: string;
    category: string;
    orderer: string;
    contractType: string;
    settingStaff: string;
    statusFilter: string;
    onepass: string;
    emergencyBell: string;
    onSearchChange: (value: string) => void;
    onCategoryChange: (value: string) => void;
    onOrdererChange: (value: string) => void;
    onContractTypeChange: (value: string) => void;
    onSettingStaffChange: (value: string) => void;
    onStatusFilterChange: (value: string) => void;
    onOnepassChange: (value: string) => void;
    onEmergencyBellChange: (value: string) => void;
    onReset: () => void;
}

const selectClass =
    'px-3 py-2 text-sm border border-[#E6E9ED] rounded bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition';

/** 다중 선택 드롭다운 */
function MultiSelect({ value, onChange, options, placeholder }: {
    value: string;
    onChange: (value: string) => void;
    options: string[];
    placeholder: string;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const selected = value ? value.split(',') : [];

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const toggle = (opt: string) => {
        const next = selected.includes(opt)
            ? selected.filter((s) => s !== opt)
            : [...selected, opt];
        onChange(next.join(','));
    };

    const label = selected.length === 0
        ? placeholder
        : selected.length <= 2
            ? selected.join(', ')
            : `${selected[0]} 외 ${selected.length - 1}건`;

    return (
        <div className="relative" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className={`${selectClass} min-w-[120px] text-left truncate`}
            >
                <span className={selected.length === 0 ? 'text-gray-500' : ''}>{label}</span>
            </button>
            {open && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-[#E6E9ED] rounded shadow-lg z-50 min-w-[160px] max-h-[240px] overflow-y-auto">
                    {options.map((opt) => (
                        <label
                            key={opt}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                        >
                            <input
                                type="checkbox"
                                checked={selected.includes(opt)}
                                onChange={() => toggle(opt)}
                                className="rounded border-gray-300 text-[#1ABB9C] w-3.5 h-3.5"
                            />
                            <span className="text-gray-700">{opt}</span>
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SearchFilterBar({
    search,
    category,
    orderer,
    contractType,
    settingStaff,
    statusFilter,
    onepass,
    emergencyBell,
    onSearchChange,
    onCategoryChange,
    onOrdererChange,
    onContractTypeChange,
    onSettingStaffChange,
    onStatusFilterChange,
    onOnepassChange,
    onEmergencyBellChange,
    onReset,
}: SearchFilterBarProps) {
    const [showHistory, setShowHistory] = useState(false);
    const [history, setHistory] = useState<string[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // 히스토리 로드
    const refreshHistory = useCallback(() => {
        setHistory(getHistory());
    }, []);

    // 입력 포커스 시 히스토리 표시
    const handleFocus = () => {
        refreshHistory();
        setShowHistory(true);
    };

    // 외부 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node) &&
                inputRef.current &&
                !inputRef.current.contains(e.target as Node)
            ) {
                setShowHistory(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 검색어 입력 시 히스토리 필터링
    const filteredHistory = search
        ? history.filter((h) => h.toLowerCase().includes(search.toLowerCase()))
        : history;

    // 히스토리 항목 선택
    const handleSelectHistory = (term: string) => {
        onSearchChange(term);
        setShowHistory(false);
        inputRef.current?.blur();
    };

    // 히스토리 항목 삭제
    const handleRemoveHistory = (e: React.MouseEvent, term: string) => {
        e.stopPropagation();
        removeFromHistory(term);
        refreshHistory();
    };

    // Enter 키로 검색 히스토리 저장
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && search.trim()) {
            addToHistory(search.trim());
            setShowHistory(false);
            inputRef.current?.blur();
        }
        if (e.key === 'Escape') {
            setShowHistory(false);
        }
    };

    // 구분에 따른 연동 옵션
    const categoryConfig = category ? CATEGORY_OPTIONS_MAP[category] : null;
    const ordererOptions = categoryConfig ? categoryConfig.orderers : ALL_ORDERERS;
    const contractOptions = categoryConfig ? categoryConfig.contractTypes : ALL_CONTRACT_TYPES;

    return (
        <div className="bg-white rounded shadow-sm border border-[#E6E9ED] p-4 mb-5">
            <div className="flex items-center gap-3 flex-wrap">
                {/* 검색 입력 + 히스토리 드롭다운 */}
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 z-10" />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="단지명 또는 주소로 검색..."
                        value={search}
                        onChange={(e) => {
                            onSearchChange(e.target.value);
                        }}
                        onFocus={handleFocus}
                        onKeyDown={handleKeyDown}
                        className="w-full pl-9 pr-4 py-2 text-sm border border-[#E6E9ED] rounded focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition"
                    />

                    {/* 히스토리 드롭다운 */}
                    {showHistory && filteredHistory.length > 0 && (
                        <div
                            ref={dropdownRef}
                            className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E6E9ED] rounded shadow-lg z-50 overflow-hidden"
                        >
                            <div className="px-3 py-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-wider border-b border-gray-100">
                                최근 검색
                            </div>
                            {filteredHistory.map((term) => (
                                <div
                                    key={term}
                                    onClick={() => handleSelectHistory(term)}
                                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer transition group"
                                >
                                    <Clock className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                                    <span className="text-sm text-gray-600 flex-1 truncate">{term}</span>
                                    <button
                                        onClick={(e) => handleRemoveHistory(e, term)}
                                        className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-gray-200 rounded transition"
                                    >
                                        <X className="w-3 h-3 text-gray-400" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 상태 필터 */}
                <select value={statusFilter} onChange={(e) => onStatusFilterChange(e.target.value)} className={selectClass}>
                    <option value="">관리 중</option>
                    <option value="excluded">제외된 단지</option>
                </select>

                {/* 구분 */}
                <select value={category} onChange={(e) => onCategoryChange(e.target.value)} className={selectClass}>
                    <option value="">구분 전체</option>
                    {ALL_CATEGORIES.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                    <option value="미분류">미분류</option>
                </select>

                {/* 발주처 */}
                <select value={orderer} onChange={(e) => onOrdererChange(e.target.value)} className={selectClass}>
                    <option value="">발주처 전체</option>
                    {ordererOptions.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                </select>

                {/* 계약 */}
                <MultiSelect
                    value={contractType}
                    onChange={onContractTypeChange}
                    options={contractOptions}
                    placeholder="계약 전체"
                />

                {/* 세팅 담당자 */}
                <select value={settingStaff} onChange={(e) => onSettingStaffChange(e.target.value)} className={selectClass}>
                    <option value="">세팅 담당자</option>
                    {STAFF_OPTIONS.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                </select>

                {/* 원패스 */}
                <MultiSelect
                    value={onepass}
                    onChange={onOnepassChange}
                    options={['미사용', '마이타운', '자이패스', '오이패스', '노크타운', '스마트원', '아파트너', '아파트스토리', '스마트패스', '코콤']}
                    placeholder="원패스 전체"
                />

                {/* 비상벨 */}
                <select value={emergencyBell} onChange={(e) => onEmergencyBellChange(e.target.value)} className={selectClass}>
                    <option value="">비상벨 전체</option>
                    <option value="used">사용</option>
                    <option value="unused">미사용</option>
                </select>

                {/* 필터 초기화 */}
                <button onClick={onReset} className="p-2 text-gray-400 hover:text-gray-600 transition" title="필터 초기화">
                    <RotateCcw className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
